import React, { useEffect, useMemo, useContext } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';

import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import AnalyticsInstrumentedDiffSet from 'src/components/diff-set/analytics-diff-set';
import { UNLOAD_DIFF_DIFFSTAT } from 'src/redux/pull-request/actions';
import {
  getDiffErrorCode,
  getDiffFiles,
  getDiffStatErrorCode,
  getDiffStatErrorKey,
  getIsPullRequestTruncated,
  getIsSingleFileModeActive,
} from 'src/redux/pull-request/selectors';
import { Loading } from 'src/sections/global/components/loading.style';
import { DiffErrors } from 'src/sections/repository/sections/pull-request/components/diff-errors';
import PartialDiffMessage from 'src/sections/repository/sections/pull-request/containers/partial-diff-message';

import { DiffContext } from './diff-provider';
import NoFilesMessage from './no-files-message';

export type DiffLoaderProps = {
  NoDiffComponent?: React.ReactElement;
  onDiffLoaded?: () => void;
  onDiffError?: (errorCode: string | number) => JSX.Element | null;
  onDiffSuccess?: () => JSX.Element;
  tabbedView?: boolean;
  iterativeReviewSelectorLoading?: boolean;
};

const DiffMessageContainer = styled.div({
  marginBottom: token('space.200', '16px'),
});

/* eslint @typescript-eslint/ban-types: "warn" */
export const DiffLoader: React.FC<DiffLoaderProps> = ({
  NoDiffComponent = <NoFilesMessage />,
  onDiffLoaded = () => {},
  onDiffError = () => {},
  onDiffSuccess = () => {},
  tabbedView,
  iterativeReviewSelectorLoading = false,
}) => {
  const {
    loadDiff,
    loadDiffStat,
    isLoadingDiff,
    isLoadingDiffStat,
    diffMetadataProps,
  } = useContext(DiffContext);
  useEffect(loadDiff, [loadDiff]);
  useEffect(loadDiffStat, [loadDiffStat]);

  const dispatch = useDispatch();

  // remove diff/diffstat data from redux on unmount
  useEffect(() => {
    return () => {
      // skip on tabbed view, since data is needed and may not be refetched across tab navigation
      if (!tabbedView) {
        dispatch({ type: UNLOAD_DIFF_DIFFSTAT });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const diff = useSelector(getDiffFiles);

  const diffErrorCode = useSelector(getDiffErrorCode);
  const diffStatErrorCode = useSelector(getDiffStatErrorCode);
  const diffStatErrorKey = useSelector(getDiffStatErrorKey);
  const generalErrorCode = diffErrorCode || diffStatErrorCode;

  const isTruncated = useSelector(getIsPullRequestTruncated);
  const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);

  useMemo(() => {
    if (!isLoadingDiff && !isLoadingDiffStat && onDiffLoaded) {
      onDiffLoaded();
    }
  }, [isLoadingDiff, isLoadingDiffStat, onDiffLoaded]);

  if (isLoadingDiff || isLoadingDiffStat || iterativeReviewSelectorLoading) {
    return (
      <Loading>
        <Spinner
          size="medium"
          testId="diff-loading-spinner"
          interactionName="diff-loader-spinner"
        />
      </Loading>
    );
  } else if (generalErrorCode) {
    return (
      <>
        <DiffErrors
          errorCodeString={`${generalErrorCode}`}
          errorKey={diffStatErrorKey || null}
        />
        {onDiffError(generalErrorCode)}
      </>
    );
  } else if (diff && diff.length === 0) {
    return (
      <>
        {NoDiffComponent}
        {onDiffSuccess()}
      </>
    );
  } else {
    return (
      <>
        {!isSingleFileModeActive && isTruncated && (
          <DiffMessageContainer>
            <PartialDiffMessage />
          </DiffMessageContainer>
        )}
        <AnalyticsInstrumentedDiffSet
          files={diff}
          diffMetadataProps={diffMetadataProps}
          tabbedView={tabbedView}
        />
        {onDiffSuccess()}
      </>
    );
  }
};
